export const en = {
  brand: { name: "SmartPaw", tagline: "Personal cabinet" },
  language: { ka: "ქართული", en: "English", short_ka: "KA", short_en: "EN", toggle_aria: "Toggle language" },
  nav: {
    dashboard: "Dashboard", subscription: "Subscriptions", subscriptions: "Subscriptions",
    catalogue: "Partner brands", offers: "Special offers", pets: "Pets",
    calendar: "Pet calendar", orders: "Orders",
    requests: "My requests",
    deliveries: "My deliveries",
    payments: "Payments",
    addresses: "Addresses", profile: "Profile", notifications: "Notifications",
    logout: "Log out", account: "Account"
  },
  auth: {
    sign_in: "Sign in", sign_up: "Create account",
    email: "Email", password: "Password", password_repeat: "Repeat password", name: "Full name",
    welcome_back: "Welcome back", create_account: "Create your account",
    login_subtitle: "Manage your SmartPaw cabinet",
    register_subtitle: "Set up your account in a few steps",
    continue_with_google: "Continue with Google",
    or_continue_with: "Or continue with email",
    forgot_password: "Forgot password?",
    no_account: "No account yet?", have_account: "Have an account?",
    register_now: "Register", sign_in_now: "Sign in",
    submitting: "Submitting..."
  },
  wizard: {
    step: "Step {n} of {total}",
    next: "Continue", back: "Back", skip: "Skip",
    finish: "Finish & create account",
    step1_title: "Create your account",
    step1_subtitle: "Your name, email and a password",
    step2_title: "Contact & delivery",
    step2_subtitle: "Where should we deliver your boxes?",
    step3_title: "Your first pet",
    step3_subtitle: "Tell us about your pet (optional)",
    step4_title: "Your first subscriptions",
    step4_subtitle: "Pick products and frequency — up to 3 separate subscriptions, each with its own products and delivery date.",
    add_subscription: "Add subscription",
    remove_subscription: "Remove",
    subscription_n: "Subscription #{n}",
    first_delivery: "First delivery date",
    pick_products: "Choose products",
    label_placeholder: "Name it (e.g. 'Bibo's food')",
    pet_optional: "You can skip and add later from the cabinet",
    sub_optional: "You can skip and add later from the cabinet",
    no_products_yet: "No products selected yet.",
    err_name_required: "Name is required",
    err_email_required: "Email is required",
    err_password_short: "Password must be at least 6 characters",
    err_password_mismatch: "Passwords do not match",
    err_phone_required: "Please enter a valid phone number",
    phone_hint: "We use this only for delivery updates and your SmartPaw expert."
  },
  dashboard: {
    title: "Welcome",
    subtitle: "Here are your upcoming deliveries and quick actions.",
    next_delivery: "Next delivery",
    no_subscription: "You don't have an active subscription.",
    start_now: "Start a subscription",
    in_box: "In the next box", edit_upcoming: "Edit",
    pause: "Pause", resume: "Resume",
    quick_pets: "Your pets", quick_address: "Default delivery address",
    recent_orders: "Recent orders", see_all: "View all",
    add_pet: "Add a pet", add_address: "Add address",
    no_pets: "No pets yet — add one to personalize.",
    no_addresses: "Add your first delivery address.",
    no_orders: "No past orders yet.",
    countdown_days: "days", countdown_hours: "hours", countdown_minutes: "min",
    paused_label: "Paused", cancelled_label: "Cancelled",
    your_subscriptions: "Your subscriptions",
    offers_strip_title: "Special offers for you",
    offers_view_all: "View all offers"
  },
  subscription: {
    title: "Subscriptions",
    subtitle: "Manage frequencies, products and addresses — up to 3 active subscriptions.",
    add_new: "Add subscription",
    max_reached: "You've reached the limit of 3 active subscriptions.",
    delete: "Delete", delete_confirm_title: "Delete this subscription?",
    delete_confirm_text: "This action cannot be undone.",
    deleted_toast: "Subscription deleted",
    name_label: "Subscription name",
    frequency: "Delivery frequency",
    weekly: "Every week", biweekly: "Every 2 weeks", monthly: "Every month",
    delivery_address: "Delivery address",
    next_delivery: "Next delivery", change_date: "Change date",
    items: "Products", qty: "Qty",
    add_more: "Add products", save: "Save changes",
    pause: "Pause", resume: "Resume", cancel: "Cancel subscription",
    cancel_confirm_title: "Cancel subscription?",
    cancel_confirm_text: "You can restart it later from your cabinet.",
    cancel_confirm_ok: "Yes, cancel", cancel_back: "Keep it",
    saved_toast: "Subscription saved",
    paused_toast: "Subscription paused",
    resumed_toast: "Subscription resumed",
    cancelled_toast: "Subscription cancelled",
    no_items: "Add products from the catalogue.",
    remove: "Remove",
    no_address_warning: "Please add a delivery address first.",
    status: { active: "Active", paused: "Paused", cancelled: "Cancelled" }
  },
  catalogue: {
    title: "Catalogue", subtitle: "Add products to your upcoming order.",
    all: "All", food: "Food", hygiene: "Hygiene", vitamins: "Vitamins & Supplements",
    search_placeholder: "Search the catalogue...",
    add_to_upcoming: "Add to box",
    in_subscription: "In your box",
    added_toast: "Added to your box",
    no_results: "No products matched your search.",
    suitable: { dog: "Dogs", cat: "Cats", both: "Dogs & Cats" },
    choose_subscription: "Choose a subscription",
    choose_subscription_desc: "Pick which subscription this product should join.",
    add_button: "Add"
  },
  offers: {
    title: "Special offers",
    subtitle: "Exclusive discounts and curated bundles for your subscriptions.",
    add_to_box: "Add to my box",
    add_to_subscription: "Choose a subscription for this offer",
    applied_toast: "Offer added to your box",
    includes: "Includes:",
    no_offers: "No active offers right now.",
    expires: "Expires", saves: "Save"
  },
  pets: {
    title: "Your pets",
    subtitle: "Tailor recommendations by adding each pet.",
    add: "Add pet", edit: "Edit", delete: "Delete",
    name: "Name", species: "Species", dog: "Dog", cat: "Cat",
    breed: "Breed", birth_date: "Birth date",
    weight_kg: "Weight (kg)", photo: "Photo",
    photo_url: "Photo URL",
    approx_age: "Approximate age (if birth date unknown)",
    age_label: "Age",
    birth_certificate: "Birth certificate (PDF/JPG, optional)",
    dietary_notes: "Dietary notes",
    save: "Save", cancel: "Cancel",
    saved_toast: "Pet saved", deleted_toast: "Pet removed",
    confirm_delete: "Remove this pet?", no_pets: "No pets yet.",
    vet_section: "Vet contact (optional)",
    vet_hint: "We'll show this on every Vet visit event in your calendar.",
    vet_name: "Vet name",
    vet_clinic: "Clinic",
    vet_phone: "Phone",
    vet_email: "Email",
    vet_address: "Address",
    vet_notes: "Notes"
  },
  photo: {
    upload: "Upload photo", take_photo: "Take a photo",
    drop_or_click: "Drop a photo here or click to choose",
    camera_open: "Open camera", capture: "Capture",
    retake: "Retake", use_photo: "Use this photo",
    close: "Close",
    camera_error: "Couldn't access camera. Check browser permissions."
  },
  file: { upload_hint: "Drop a file here or click to choose (JPG/PNG/PDF)" },
  quiz: {
    title: "Quick quiz",
    subtitle: "Optional — helps us tailor your recommendations.",
    open_button: "Take the quiz",
    completed: "Quiz completed",
    saved_toast: "Quiz saved",
    save: "Save quiz",
    q1: "How much time do you spend with your pet per day?",
    time_lt1: "Less than 1 hour", time_1to3: "1–3 hours", time_3to6: "3–6 hours", time_gt6: "6+ hours",
    q2: "How many pets do you have at home?",
    q3: "Do you feed dry food only, or combine with wet food/snacks?",
    food_dry_only: "Dry only", food_dry_wet: "Dry + wet",
    food_dry_snacks: "Dry + snacks", food_dry_wet_snacks: "Dry + wet + snacks",
    q4: "How often do you give snacks?",
    snack_never: "Never", snack_weekly: "Weekly",
    snack_daily: "Daily", snack_multiple_daily: "Multiple times daily",
    q5: "Does your pet have allergies?",
    allergies_no: "No", allergies_yes: "Yes", allergies_unknown: "Not sure",
    allergies_placeholder: "Which ingredients?",
    q6: "Which dry food flavor does your pet prefer?",
    flavor_chicken: "Chicken", flavor_beef: "Beef", flavor_fish: "Fish",
    flavor_lamb: "Lamb", flavor_turkey: "Turkey", flavor_mixed: "Mixed",
    flavor_none: "No preference"
  },
  payment: {
    title: "Payment method",
    bank_transfer: "Bank transfer",
    cash_on_delivery: "Cash on delivery",
    method_label: "Payment"
  },
  addresses: {
    title: "Addresses", subtitle: "Manage delivery addresses and set a default.",
    add: "Add address", label: "Label", recipient: "Recipient",
    phone: "Phone", city: "City", district: "District",
    street: "Street", building: "Building", apartment: "Apartment",
    postal_code: "Postal code", notes: "Notes",
    default: "Default", set_default: "Set as default",
    edit: "Edit", delete: "Delete", save: "Save", cancel: "Cancel",
    saved_toast: "Address saved", deleted_toast: "Address deleted",
    no_addresses: "You have no addresses yet."
  },
  orders: {
    title: "Orders", subtitle: "Your delivery history and invoices.",
    invoice: "Invoice", date: "Date", items: "Items",
    total: "Total", status: "Status", view: "View",
    delivered: "Delivered", processing: "Processing",
    scheduled: "Scheduled", cancelled: "Cancelled",
    no_orders: "No orders yet.",
    subtotal: "Subtotal", delivery_fee: "Delivery",
    free: "Free", delivery_to: "Delivering to:", qty_short: "x"
  },
  requests: {
    title: "My requests",
    subtitle: "Track the status of products you've asked us to source.",
    new_request: "Request a product",
    empty: "You haven't requested any products yet. If a product you need isn't in our catalogue, ask us to source it for you.",
    view: "View",
    no_details: "No additional details",
    qty_short: "x",
    request_id: "Request ID",
    admin_note: "Note from SmartPaw",
    contact_hint: "We'll get back to you on:",
    status: {
      pending: "Pending review",
      sourced: "Sourced — ready to order",
      ordered: "Ordered for you",
      declined: "Couldn't source"
    },
    col: {
      status: "Status",
      submitted: "Submitted",
      brand: "Brand",
      size: "Size / pack",
      quantity: "Quantity",
      notes: "Notes",
      photo: "Photo"
    }
  },
  profile: {
    title: "Profile", subtitle: "Update your account information.",
    name: "Name", email: "Email", phone: "Phone",
    member_since: "Member since", save: "Save",
    saved_toast: "Profile updated", sign_in_method: "Sign-in method"
  },
  notifications: {
    title: "Notifications", subtitle: "Choose how and when to hear from us.",
    channels: "Channels", types: "Types",
    email_enabled: "Email", sms_enabled: "SMS", whatsapp_enabled: "WhatsApp",
    delivery_reminders: "Delivery reminders",
    daily_digest_enabled: "Daily pet-care digest (8:00 Tbilisi time)",
    low_stock_alerts: "Low-stock alerts",
    marketing: "Offers & news",
    saved_toast: "Preferences saved"
  },
  common: {
    loading: "Loading...", saving: "Saving...",
    retry: "Try again", error_generic: "Something went wrong. Please try again.",
    confirm: "Confirm", close: "Close", currency: "GEL",
    next_box_in: "Your next box arrives in:",
    optional: "optional", required: "required"
  },
  calendar: {
    title: "Pet Care Calendar",
    subtitle: "Plan medications, grooming, vet visits, food deliveries and more — all in one place.",
    add_event: "Add event",
    new_event: "New event",
    edit_event: "Edit event",
    today: "Today",
    view_month: "Month",
    view_list: "Upcoming",
    all_pets: "All pets",
    all_categories: "All categories",
    no_pet: "No specific pet",
    field_title: "Title",
    field_category: "Category",
    field_pet: "Pet",
    field_when: "When",
    field_duration: "Duration (min)",
    field_repeat: "Repeat",
    field_until: "Repeat until (optional)",
    field_notes: "Notes",
    field_remind_when: "Send",
    reminders: "Reminders",
    remind_none: "No reminder",
    remind_1h: "1 hour before",
    remind_24h: "24 hours before",
    via_email: "Email",
    via_whatsapp: "WhatsApp",
    whatsapp_pending: "Coming soon",
    repeat_none: "Does not repeat",
    repeat_daily: "Daily",
    repeat_weekly: "Weekly",
    repeat_monthly: "Monthly",
    repeat_yearly: "Yearly",
    title_placeholder: "e.g. Heartworm pill",
    notes_placeholder: "Dosage, vet name, etc.",
    title_required: "Title is required",
    confirm_delete: "Delete this event?",
    event_created: "Event added",
    event_updated: "Event updated",
    event_deleted: "Event deleted",
    marked_done: "Marked done",
    read_only_event: "This event is auto-generated from a subscription and can't be edited here.",
    empty_upcoming: "No upcoming events. Add one to get started.",
    cancel: "Cancel",
    save: "Save changes",
    create: "Add event",
    delete: "Delete",
    export_ics: ".ics",
    ics_downloaded: "Calendar file downloaded — import into Google / Apple Calendar.",
    auto: "auto",
    dashboard_widget_title: "Next pet-care tasks",
    dashboard_widget_view_all: "View calendar",
    dashboard_widget_empty: "No upcoming tasks. Add a reminder to get started.",
    use_template: "Use template",
    tpl_picker_title: "Pre-built schedules",
    tpl_picker_subtitle: "Set up a complete care schedule in one click — based on WSAVA/AAHA international standards.",
    tpl_events_count: "events",
    tpl_for_dog: "Dogs",
    tpl_for_cat: "Cats",
    tpl_back: "Back",
    tpl_apply: "Add {count} events",
    tpl_anchor_required: "Please pick a date",
    tpl_anchor_hint_birth: "Events will be calculated relative to this date (e.g. vaccine at week 8).",
    tpl_anchor_hint_start: "Events will start from this date.",
    tpl_no_matching_pet: "This template is designed for a different species.",
    tpl_reminders_default: "Reminders will be set to 24h email by default — you can adjust per event after.",
    tpl_created: "Added {count} events to your calendar",
    stat_streak: "On-time streak",
    stat_done: "Tasks done",
    stat_rate: "Completion rate",
    vet_contact: "Vet contact",
    attachments: "Attachments",
    attach_upload: "Attach receipt / photo",
    attach_hint: "Images or PDF receipts, up to 6 MB.",
    attach_added: "File attached",
    attach_removed: "Attachment removed"
  },
  phoneGate: {
    title: "One more step",
    subtitle: "We need your phone number so we can reach you about deliveries and consultations.",
    field_label: "Phone number",
    hint: "Georgian mobile number — we'll only use it for important order updates.",
    save: "Save and continue",
    saved: "Thanks! Phone number saved.",
    invalid: "Please enter a valid Georgian phone number"
  },
  limbo: {
    pending_admin_review: {
      eyebrow: "Registration received",
      title: "Thanks, {name}! We're reviewing your registration",
      subtitle: "The SmartPaw team is reviewing your profile and preparing tailored pricing + a delivery schedule. This usually takes up to one business day — we'll reach out via WhatsApp or email."
    },
    pending_customer_confirmation: {
      eyebrow: "Review & confirm",
      title: "Your plan is ready, {name}",
      subtitle: "We've added pricing, products and a delivery schedule for you. Review the details below and either confirm to activate your account, or request changes."
    },
    revisions_requested: {
      eyebrow: "Changes requested",
      title: "Your change request has been sent",
      subtitle: "The SmartPaw team will revise your plan and send the updated version for you to confirm."
    },
    rejected: {
      eyebrow: "Offer rejected",
      title: "Thanks for your feedback",
      subtitle: "Your rejection is archived. If you change your mind later, email us at info@smartpaw.ge."
    },
    admin_notes_title: "Notes from the SmartPaw team",
    profile_summary_title: "Your profile",
    field_name: "Name",
    field_email: "Email",
    field_phone: "Phone",
    section_pets: "Pets ({count})",
    section_addresses: "Addresses ({count})",
    section_subs: "Subscriptions ({count})",
    section_subs_item: "Subscription",
    items_short: "item(s)",
    default_badge: "default",
    confirm_button: "Confirm & activate",
    request_changes_button: "Request changes",
    reject_button: "Reject offer",
    confirm_success: "Excellent — your account is now active.",
    request_message_required: "Please describe what should change",
    request_sent: "Change request sent",
    reject_reason_required: "Please choose a reason",
    reject_sent: "Thanks for the feedback — the offer has been rejected.",
    request_dialog_title: "What needs to change?",
    request_dialog_subtitle: "Let us know which detail to adjust — price, products, delivery frequency, address, or anything else.",
    request_dialog_placeholder: "e.g. please change frequency to every 2 weeks…",
    request_dialog_cancel: "Cancel",
    request_dialog_submit: "Send request",
    reject_dialog_title: "Reject the offer",
    reject_dialog_subtitle: "A quick note helps us improve future proposals.",
    reject_dialog_cancel: "Cancel",
    reject_dialog_submit: "Confirm rejection",
    reject_reason_label: "Reason",
    reject_reason_placeholder: "Choose a reason",
    reject_reason_price_too_high: "Price is too high",
    reject_reason_products_not_suitable: "Products don't suit us",
    reject_reason_delivery_schedule_mismatch: "Delivery schedule doesn't work",
    reject_reason_found_alternative: "Found an alternative",
    reject_reason_not_ready_yet: "Not ready yet",
    reject_reason_other: "Other",
    reject_note_label: "Additional note (optional)",
    reject_note_placeholder: "Any extra context…",
    footer_note: "If you have any questions, reach out on WhatsApp or at info@smartpaw.ge."
  },
  deliveries: {
    title: "My deliveries",
    subtitle: "Upcoming deliveries and your delivery history",
    empty: "No deliveries yet — once your subscription is active, deliveries will appear here.",
    upcoming_title: "Upcoming",
    history_title: "History",
    confirm_reminder: "Confirm delivery",
    confirm_received: "I received it",
    confirmed_toast: "Thanks! Delivery confirmed. Payment instructions are on the way.",
    confirmed_badge: "Confirmed",
    delivered_badge: "Delivered",
    paid: "Paid"
  },
  payments: {
    title: "Payments",
    subtitle: "Payment history, outstanding balances, and our bank details",
    unpaid_title: "Outstanding",
    unpaid_badge: "Due",
    bank_title: "Where to pay",
    bank_name: "Bank",
    account_holder: "Account holder",
    bank_code: "Bank code",
    history_title: "Payment history",
    history_empty: "No payments recorded yet.",
    paid_badge: "Paid",
    reference_label: "Reference"
  }
};
